// Client-side helpers for the BuildDeck image-attachment input.
// Downscale + format-normalize in the browser so the wire payload is
// small (server caps at 2MB/image), then base64-encode for the JSON body.

export const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"] as const;
export type AllowedImageType = (typeof ALLOWED_IMAGE_TYPES)[number];

export const MAX_IMAGES = 4;
/** Max long edge after downscale. Sonnet's vision tile boundary is around
 *  this size; going larger doesn't materially improve OCR for MTGA UI. */
export const MAX_DIMENSION = 1024;
/** Hard cap on the encoded JPEG to keep the JSON body small. The server
 *  enforces 2MB; we aim well below to leave headroom for multiple images. */
export const TARGET_BYTES = 900_000;

export interface AttachedImage {
  /** Stable id for React keys + remove button. */
  id: string;
  /** Display thumbnail (object URL of the downscaled blob). */
  preview_url: string;
  /** Final base64 (no `data:` prefix) sent to the server. */
  data_b64: string;
  /** Final media type after normalization. Always `image/jpeg` from
   *  `processFile` because we re-encode for predictable size. */
  media_type: AllowedImageType;
  /** Human-readable filename for the thumbnail tooltip. */
  filename: string;
  /** Final encoded byte count, for UI display. */
  bytes: number;
}

function isAllowedType(type: string): type is AllowedImageType {
  return (ALLOWED_IMAGE_TYPES as readonly string[]).includes(type);
}

async function loadImage(blob: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("could not decode image"));
      img.src = url;
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("failed to read blob"));
    reader.onload = () => {
      const result = reader.result as string;
      // result is `data:<type>;base64,<payload>` — strip the prefix.
      const idx = result.indexOf(",");
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * Normalize a user-provided File/Blob into an AttachedImage by:
 *   1. Rejecting unsupported MIME types.
 *   2. Drawing onto a canvas downscaled so the long edge ≤ MAX_DIMENSION.
 *   3. Re-encoding as JPEG, dropping quality until under TARGET_BYTES
 *      (so multi-image submits don't blow the server's 2MB cap).
 *
 * Throws on decode failure so the caller can surface a useful error.
 */
export async function processFile(file: File): Promise<AttachedImage> {
  if (!isAllowedType(file.type)) {
    throw new Error(`unsupported image type: ${file.type || "unknown"}`);
  }
  const img = await loadImage(file);

  const longEdge = Math.max(img.naturalWidth, img.naturalHeight);
  const scale = longEdge > MAX_DIMENSION ? MAX_DIMENSION / longEdge : 1;
  const w = Math.round(img.naturalWidth * scale);
  const h = Math.round(img.naturalHeight * scale);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas 2d context unavailable");
  // White background so PNGs with transparency don't render as black
  // when re-encoded to JPEG. MTGA screenshots are opaque so this is a
  // no-op for the common case.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);

  // Walk quality down until we're under the target. Most MTGA caps come
  // in well under TARGET_BYTES at q=0.85, so this typically only loops
  // once. Cap at 4 attempts so a pathological input can't hang.
  let quality = 0.85;
  let blob: Blob | null = null;
  for (let attempt = 0; attempt < 4; attempt++) {
    blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", quality),
    );
    if (!blob) throw new Error("canvas.toBlob returned null");
    if (blob.size <= TARGET_BYTES) break;
    quality -= 0.15;
  }
  if (!blob) throw new Error("failed to encode image");

  const data_b64 = await blobToBase64(blob);
  const preview_url = URL.createObjectURL(blob);

  return {
    id: `img_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    preview_url,
    data_b64,
    media_type: "image/jpeg",
    filename: file.name || "image.jpg",
    bytes: blob.size,
  };
}

/** Drop the object URL backing the thumbnail so the browser can GC the
 *  underlying blob. Call from React cleanup paths. */
export function disposeAttachment(att: AttachedImage): void {
  URL.revokeObjectURL(att.preview_url);
}
